import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { addUserInfo, resetUserInfo } from '../store/user'
import httpClient from './helpers/httpClient'
import CircularProgress from '@mui/material/CircularProgress'

/**
 * ProtectedRoute component that checks if user is authenticated.
 * If not authenticated, redirects to login page.
 * If authenticated, renders the children.
 */
function ProtectedRoute({ children }) {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const location = useLocation()
  const dispatch = useDispatch()
  const userInfo = useSelector(state => state.user)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    // Always verify with the backend to ensure token/session is still valid
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/auth/check")
      
      if (response.status === 200 && response.data.authenticated) {
        // Store/update user info in Redux
        dispatch(addUserInfo(response.data.user))
        setIsAuthenticated(true)
      } else {
        // Clear any stale user data
        dispatch(resetUserInfo())
        setIsAuthenticated(false)
      }
    } catch (error) {
      // If we get a 401 or any error, user is not authenticated
      // Clear any stale user data from Redux
      dispatch(resetUserInfo())
      setIsAuthenticated(false)
    }
    
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <CircularProgress />
      </div>
    )
  }

  if (!isAuthenticated) {
    const redirectTo = `${location.pathname}${location.search}${location.hash}`

    // Redirect to login page, preserving the intended destination
    return <Navigate to={`/?redirect=${encodeURIComponent(redirectTo)}`} state={{ from: location }} replace />
  }

  return children
}

export default ProtectedRoute

